import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  ListPayoutsOptions,
  ListPayoutsResponse,
  Payout,
  PayoutCreatedEnvelope,
  PayoutUpdatedEnvelope,
  getPayout as universalGetPayout,
  listPayouts as universalListPayouts,
} from './payments-payouts-v4-payout-payouts.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/payments' };

export function listPayouts(httpClient: HttpClient): ListPayoutsSignature {
  return (accountId: string, options?: ListPayoutsOptions) =>
    universalListPayouts(
      accountId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListPayoutsSignature {
  /**
   * Retrieves a paginated list of payouts for the specified Wix Payments account.
   *
   * Supports optional filters by account profile and by creation date range.
   * A newly created payout can take a short time to appear in the results.
   *
   * Calls are limited to 100 requests per 10 seconds per caller. Requests
   * over the limit fail with a `RESOURCE_EXHAUSTED` error and the message
   * `The request was throttled`.
   * @param - ID of the Wix Payments account whose payouts to list. Retrieve account
   * IDs from the Accounts API.
   */
  (accountId: string, options?: ListPayoutsOptions): Promise<
    NonNullablePaths<
      ListPayoutsResponse,
      | `payouts`
      | `payouts.${number}._id`
      | `payouts.${number}.accountId`
      | `payouts.${number}.accountProfileId`
      | `payouts.${number}.amount.value`
      | `payouts.${number}.amount.currency`
      | `payouts.${number}.status`
      | `payouts.${number}.failureReason.code`
      | `payouts.${number}.cashAdvanceIncluded`,
      3
    >
  >;
}

export function getPayout(httpClient: HttpClient): GetPayoutSignature {
  return (payoutId: string, accountId: string) =>
    universalGetPayout(
      payoutId,
      accountId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetPayoutSignature {
  /**
   * Retrieves a single payout by ID.
   *
   * Calls are limited to 100 requests per 10 seconds per caller. Requests
   * over the limit fail with a `RESOURCE_EXHAUSTED` error and the message
   * `The request was throttled`.
   * @param - ID of the payout to retrieve.
   * @param - ID of the Wix Payments account that owns the payout. The payout must
   * belong to this account. Retrieve account IDs from the Accounts API.
   * @returns Retrieved payout.
   */
  (payoutId: string, accountId: string): Promise<
    NonNullablePaths<
      Payout,
      | `_id`
      | `accountId`
      | `accountProfileId`
      | `amount.value`
      | `amount.currency`
      | `status`
      | `failureReason.code`
      | `cashAdvanceIncluded`,
      1
    >
  >;
}

export const onPayoutCreated = /*#__PURE__*/ EventDefinition(
  'wix.payments.payouts.v4.payout_created',
  true,
  (event: PayoutCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<PayoutCreatedEnvelope>();
export const onPayoutUpdated = /*#__PURE__*/ EventDefinition(
  'wix.payments.payouts.v4.payout_updated',
  true,
  (event: PayoutUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<PayoutUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AddressLocation,
  BaseEventMetadata,
  CreatePayoutRequest,
  CreatePayoutResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  GetPayoutGroupByPayoutIdRequest,
  GetPayoutGroupByPayoutIdResponse,
  GetPayoutGroupRequest,
  GetPayoutGroupResponse,
  GetPayoutRequest,
  GetPayoutResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ListPayoutGroupsRequest,
  ListPayoutGroupsResponse,
  ListPayoutsOptions,
  ListPayoutsRequest,
  ListPayoutsResponse,
  MessageEnvelope,
  Money,
  Paging,
  PagingMetadata,
  Payout,
  PayoutCreatedEnvelope,
  PayoutFailureCode,
  PayoutFailureReason,
  PayoutGroup,
  PayoutStatus,
  PayoutUpdatedEnvelope,
  RestoreInfo,
  SortOrder,
  Sorting,
  WebhookIdentityType,
} from './payments-payouts-v4-payout-payouts.universal.js';
